import { useCallback, useRef, type MouseEvent } from "react";
import { globalAppCloseAriaLabel } from "./copy.js";
import { useGlobalAppClose } from "./GlobalAppCloseContext.js";
import { isTelegramMiniAppContext, requestGlobalAppClose } from "./telegram-close.js";
import type {
  GlobalAppCloseAnalyticsPayload,
  GlobalAppCloseBrowserFallback,
  GlobalAppCloseLocale,
  GlobalAppCloseSurface,
} from "./types.js";

export type GlobalAppCloseButtonProps = {
  surface: GlobalAppCloseSurface;
  locale: GlobalAppCloseLocale;
  /** Optional override; defaults to context aggregate. */
  dirty?: boolean;
  onBeforeClose?: () => void;
  browserFallback: GlobalAppCloseBrowserFallback;
  /** Customer: re-show Telegram entry. Manager: keep fail-closed (no customer redirect). */
  onBrowserUiFallback?: () => void;
  routeClass?: string;
  buildId?: string;
  onAnalytics?: (payload: GlobalAppCloseAnalyticsPayload) => void;
  className?: string;
  testId?: string;
};

export function GlobalAppCloseButton({
  surface,
  locale,
  dirty: dirtyProp,
  onBeforeClose,
  browserFallback,
  onBrowserUiFallback,
  routeClass = "unknown",
  buildId = "",
  onAnalytics,
  className = "",
  testId = "global-app-close",
}: GlobalAppCloseButtonProps) {
  const { dirty: dirtyCtx } = useGlobalAppClose();
  const dirty = dirtyProp ?? dirtyCtx;
  const closingRef = useRef(false);
  const label = globalAppCloseAriaLabel(locale);

  const onClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (closingRef.current) return;
      closingRef.current = true;
      try {
        const result = await requestGlobalAppClose({
          locale,
          dirty,
          ...(onBeforeClose ? { onBeforeClose } : {}),
          onBrowserUiFallback: () => {
            if (browserFallback === "manager_fail_closed") {
              // Never redirect to customer Store V2.
              onBrowserUiFallback?.();
              return;
            }
            onBrowserUiFallback?.();
          },
        });
        try {
          onAnalytics?.({
            surface,
            route_class: routeClass,
            telegram_context: isTelegramMiniAppContext(),
            dirty,
            result,
            build_id: buildId,
          });
        } catch {
          /* analytics must never block */
        }
      } finally {
        // Allow retry if cancelled or fallback left app open.
        closingRef.current = false;
      }
    },
    [
      browserFallback,
      buildId,
      dirty,
      locale,
      onAnalytics,
      onBeforeClose,
      onBrowserUiFallback,
      routeClass,
      surface,
    ],
  );

  return (
    <button
      type="button"
      className={`ws-global-close ${className}`.trim()}
      data-testid={testId}
      data-global-app-close="true"
      data-surface={surface}
      aria-label={label}
      title={label}
      onClick={(ev) => {
        void onClick(ev);
      }}
    >
      <span className="ws-global-close-glyph" aria-hidden="true">
        ×
      </span>
    </button>
  );
}

export function useGlobalAppCloseAction() {
  const { dirty } = useGlobalAppClose();
  return { dirty, requestGlobalAppClose };
}
